import React, { Suspense } from 'react';
import { Detailed, Text } from '@react-three/drei';
import { DoubleSide, Matrix4, Vector3 } from 'three';

import { PlateManufacturer, PlatePair, PlateTypeProperties } from '@paragon/contract-library';
import {
  buildPlateShape,
  getPlatePairCenterMatrix,
  getPlatePairPlate1Matrix,
  getPlatePairPlate2Matrix,
  mth18HingeHoleRadius,
  plateSlotLength,
  smh18HingeHoleRadius,
} from '@paragon/three-shared';

import LabelVisual from './LabelVisual';

const stampedPlateTypeTextSize = 0.26;

function renderPlateMaterial() {
  return <meshBasicMaterial attach="material" color={0xa0a0a0} transparent={false} opacity={1.0} side={DoubleSide} />;
}

function getLengthHoleCount(platePair: PlatePair) {
  return Math.floor(platePair.length / plateSlotLength / 2);
}

function getManufacturerStampMatrix(platePair: PlatePair, plateTypeProperties: PlateTypeProperties) {
  const centeringShift = getLengthHoleCount(platePair) % 2 !== 0 ? plateSlotLength : 0;
  let x = platePair.length / 2;
  // The text on hinge plates is shifted so as not to be within the hinge hole
  if (platePair.plateType === 'MTH18') {
    x -= mth18HingeHoleRadius * 1.5;
  } else if (platePair.plateType === 'SMH18') {
    x -= smh18HingeHoleRadius * 1.5;
  }
  const y = platePair.width / 2;
  return (
    new Matrix4()
      .makeTranslation(centeringShift, 0, 0)
      .premultiply(
        getPlatePairCenterMatrix(platePair).multiply(
          new Matrix4().makeTranslation(x, y, platePair.distanceBetweenPlates / 2 - plateTypeProperties.thickness),
        ),
      )
      // rotate 90 degrees
      .multiply(new Matrix4().makeRotationAxis(new Vector3(0, 0, 1), Math.PI / 2))
  );
}

function renderLabel(showLabel: boolean | undefined, platePair: PlatePair) {
  if (!showLabel) return null;

  return (
    <LabelVisual
      labelText={platePair.name}
      position={{ x: platePair.centerX, y: platePair.centerY, z: platePair.centerZ + 2 }}
    />
  );
}

function getColorByPlateManufacturer(plateManufacturer: PlateManufacturer) {
  switch (plateManufacturer) {
    case 'MiTek':
      return 0xc0c0c0;
    case 'Eagle':
      return 0xdf8500;
    case 'Alpine':
      return 0x4f9c1f;
    default:
      return 0xc0c0c0;
  }
}

const stampHeight = new Matrix4().makeTranslation(0, 0, 0.1);

export function renderStamp(platePair: PlatePair, plateTypeProperties: PlateTypeProperties, baseMatrix?: Matrix4) {
  const matrix = getManufacturerStampMatrix(platePair, plateTypeProperties).premultiply(stampHeight);
  if (baseMatrix) matrix.premultiply(baseMatrix);

  return (
    <Suspense fallback={null}>
      <Detailed distances={[0, 50]} matrix={matrix} matrixAutoUpdate={false}>
        <Text
          fontSize={stampedPlateTypeTextSize}
          lineHeight={1.15}
          anchorX="center"
          anchorY="middle"
          color={getColorByPlateManufacturer(plateTypeProperties.plateManufacturer)}
        >
          {platePair.plateType}
        </Text>

        <mesh />
      </Detailed>
    </Suspense>
  );
}

interface Props {
  readonly platePair: PlatePair;
  readonly plateTypeProperties: PlateTypeProperties;
  readonly showLabel: boolean | undefined;
}

export default function PlatePairVisual(props: Props) {
  const { platePair, plateTypeProperties, showLabel } = props;

  const shape = React.useMemo(() => buildPlateShape(platePair), [platePair]);
  const extrudeGeometryOptions = {
    depth: plateTypeProperties.thickness,
    bevelEnabled: false,
  };

  return (
    <>
      <mesh key="plate1" matrixAutoUpdate={false} matrix={getPlatePairPlate1Matrix(platePair)}>
        <extrudeGeometry attach="geometry" args={[shape, extrudeGeometryOptions]} />
        {renderPlateMaterial()}
      </mesh>
      <mesh key="plate2" matrixAutoUpdate={false} matrix={getPlatePairPlate2Matrix(platePair)}>
        <extrudeGeometry attach="geometry" args={[shape, extrudeGeometryOptions]} />
        {renderPlateMaterial()}
      </mesh>
      {renderLabel(showLabel, platePair)}
      {renderStamp(platePair, plateTypeProperties)}
    </>
  );
}
